/* Cart drawer logic — fetches /cart.js, renders line items into the
 * #cart-drawer panel (chrome from site-drawer.{css,js}), wires qty steppers
 * and remove buttons to /cart/change.js, and keeps the header cart badge
 * in sync. Other components (e.g. the variants table per-row stepper)
 * listen for the `cart:updated` event dispatched on `document` to re-render
 * their own in-cart indicators.
 *
 * Public API:
 *   window.CartDrawer.open()        — open the drawer
 *   window.CartDrawer.close()       — close the drawer
 *   window.CartDrawer.refresh()     — re-fetch /cart.js and dispatch cart:updated
 *   window.CartDrawer.add(form|obj) — POST /cart/add.js then refresh
 *   window.CartDrawer.change(line, qty) — POST /cart/change.js then refresh
 *
 * Public events (on document):
 *   cart:updated { detail: { cart } } — fired after every successful refresh
 */
(function () {
  if (window.__cartDrawerInit) return;
  window.__cartDrawerInit = true;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var STRINGS = window.CartDrawerStrings || {
    empty: 'Your cart is empty',
    continueShopping: 'Continue shopping',
    remove: 'Remove',
    quantity: 'Qty',
    moneyFormat: '€{{amount}}'
  };

  // ORG-528: gr | incl | none — how this visitor's market prices relate to tax
  // (snippets/vat-mode, echoed on <body data-vat-mode> and in CartDrawerStrings).
  // Only Greece gets the ex-VAT + ×1.24 pair; elsewhere the price is already final.
  function vatMode() {
    return STRINGS.vatMode || (document.body && document.body.getAttribute('data-vat-mode')) || 'gr';
  }
  function isGreece() { return vatMode() === 'gr'; }
  // WORK-175: the staff cart sets window.StaffVatMult (1.17) while a customer on
  // ΜΕΙΩΜΕΝΟ 17% is picked; everyone else gets the standard ×1.24.
  function vatMult() { return window.StaffVatMult || 1.24; }
  function vatTag() {
    var m = vatMode();
    return (m === 'incl' ? STRINGS.vatIncl : m === 'none' ? STRINGS.vatNone : STRINGS.vatExcl) || '';
  }

  var state = { cart: null, inflight: false };

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }

  // Format money using the shop's money_format. Supports {{amount}} (2 decimals)
  // and {{amount_no_decimals}}. Locale-aware grouping/decimal separators.
  function formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '';
    var fmt = STRINGS.moneyFormat || '€{{amount}}';
    var amount = (cents / 100);
    var formatted2 = amount.toLocaleString('el-GR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    var formatted0 = Math.round(amount).toLocaleString('el-GR');
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, formatted2)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, formatted0)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, formatted2)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, formatted0);
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ORG-280: box-of-10 lines show the real piece code, not <code>-BOX10 (legacy -B10)
  // (Box10.displaySku, assets/box10.js; plain SKU if box10.js is absent).
  // ORG-713: a box line is recognised by its handle (<piece>-box10), not its SKU.
  function displaySku(item) {
    return (window.Box10 && window.Box10.displaySku) ? window.Box10.displaySku(item) : (item && item.sku);
  }

  function placeholderSvg() {
    return '<div class="cart-drawer__item-image cart-drawer__item-image--placeholder">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>' +
      '</div>';
  }

  // opts:
  //   isCompact  — render the compact "tucked under parent" treatment
  //                (small image, indented, faded title). Used for varidio.
  //   parentSku  — if set, render the companion eyebrow naming this SKU.
  //   kind       — 'varidio' or 'diakrivosi'; picks label + pill color.
  // WORK-170: when two gift codes each take part of an insert line, Shopify splits it (10 + 20).
  // variant id → {lines, qty} over the cart's lines of that variant, set by renderItems.
  var splitOf = {};
  // The price column of one line: struck compare price, «ΔΩΡΟ», the ex-VAT / incl-VAT line
  // price, the box pills, and the unit-price row. `view` (staff cart only, WORK-233) prices the
  // line after the staff discount preset so the staff drawer shows it exactly as a tier
  // customer's drawer shows Shopify's discount: {final: line cents after the preset, pct: the
  // preset's %, boxDeal: false when a box line loses its box deal (own % above the box 10%)}.
  function priceParts(item, view) {
    // WORK-264/266: a price-on-request line (0, or tagged above the threshold: window.QUOTE_ONLY)
    // says so instead of a «free» €0,00 or the hidden price; the cart is RFQ-only while it holds
    // one (assets/timologio.js).
    if (STRINGS.priceOnRequest && (item.original_price === 0 || (window.QUOTE_ONLY && window.QUOTE_ONLY.line(item)))) {
      return { line: '<span class="vat-prices__line vat-prices__line--excl" data-price0-line><em>' + escapeHtml(STRINGS.priceOnRequest).replace(' ', '<br>') + '</em></span>', unit: '' };
    }
    var GS = STRINGS.gift || {};
    var isBox10 = !!(window.Box10 && window.Box10.isBoxItem && window.Box10.isBoxItem(item));
    var finalLine = view ? view.final : item.final_line_price;
    var finalUnit = view ? Math.round(view.final / Math.max(1, item.quantity)) : item.final_price;
    // The struck price: the line before any discount (a staff extra line has only its price).
    var was = item.original_line_price || item.final_line_price;
    var comparePriceHtml = '';
    var box10OffHtml = '';
    // ORG-736: the free gift holder — a YG-DORO code took something off this line (the insert
    // lines it counted carry a 0 allocation). The word, never the colour alone.
    var giftFree = (item.line_level_discount_allocations || []).some(function (a) {
      return a && a.amount > 0 && /^YG-DORO-/i.test(String((a.discount_application && a.discount_application.title) || ''));
    });
    var giftTagHtml = giftFree ? '<span class="promo-gift-tag" data-gift-free>' + escapeHtml(GS.tag || '') + '</span>' : '';
    if (isBox10) {
      // The box's list price is 10 pieces: strike box × 10/9 (from the price
      // BEFORE any customer discount), then red «−10%» for the box and, when a
      // discount also hit the line (no tier % goes on a box since WORK-295, so only
      // a code Shopify really applied), the dark «−N%» — John's pick, option A (2026-09-29).
      var boxCompare = Math.round(item.original_line_price * 10 / 9);
      comparePriceHtml = '<span class="cart-drawer__item-compare-price price-excl-vat">' + formatMoney(boxCompare) + '</span>';
      var extraPct = 0;
      if (view) {
        extraPct = view.pct || 0;
      } else if (finalLine < item.original_line_price) {
        extraPct = 100 - Math.floor(100 * finalLine / item.original_line_price);   // = /cart's integer maths
      }
      var boxDeal = !(view && view.boxDeal === false);
      box10OffHtml = '<span class="box10-pills">' + (boxDeal ? '<span class="box10-off">−10%</span>' : '') +
        (extraPct > 0 ? '<span class="box10-cdisc">−' + extraPct + '%</span>' : '') + '</span>';
    } else if (was && was > finalLine) {
      comparePriceHtml = '<span class="cart-drawer__item-compare-price price-excl-vat">' + formatMoney(was) + '</span>' +
                        (isGreece() ? '<span class="cart-drawer__item-compare-price price-incl-vat" style="display:none;">' + formatMoney(Math.round(was * vatMult())) + '</span>' : '');
      // WORK-233 (John 2026-10-20): the customer's dark «−N%» pill, as on a box line, on every
      // discounted line but a gift (that one says «ΔΩΡΟ»). N = the staff preset, else what
      // Shopify took off the line.
      var linePct = view ? view.pct : Math.round(100 - 100 * finalLine / was);
      if (!giftFree && linePct > 0) {
        box10OffHtml = '<span class="box10-pills"><span class="box10-cdisc">−' + linePct + '%</span></span>';
      }
    }

    // Stacked with/without-VAT prices: ex-VAT (primary) + με ΦΠΑ (secondary,
    // greyed). Both always shown in Greece — the site-wide VAT toggle was removed.
    // ORG-528: outside Greece the line price is already final (incl. that country's
    // VAT, or tax-free): one line, tagged with what it is, never ×1.24.
    var linePrice = giftTagHtml +
      '<span class="vat-prices__line vat-prices__line--excl"><span class="money">' + formatMoney(finalLine) + '</span> <span class="vat-prices__tag">' + escapeHtml(vatTag()) + '</span></span>' +
      box10OffHtml +
      (isGreece() ? '<span class="vat-prices__line vat-prices__line--incl"><span class="money">' + formatMoney(Math.round(finalLine * vatMult())) + '</span> <span class="vat-prices__tag">' + escapeHtml(STRINGS.vatIncl || '') + '</span></span>' : '');

    var unitPriceHtml = '';
    if (item.quantity > 1) {
      unitPriceHtml = '<div class="cart-drawer__item-unit-price">' +
        '<span class="price-excl-vat">' + formatMoney(finalUnit) + '</span>' +
        (isGreece() ? '<span class="price-incl-vat" style="display:none;">' + formatMoney(Math.round(finalUnit * vatMult())) + '</span>' : '') +
        ' × ' + item.quantity +
      '</div>';
    }

    return { line: comparePriceHtml + linePrice, unit: unitPriceHtml };
  }

  function renderItem(item, index, opts) {
    opts = opts || {};
    var isCompact = !!opts.isCompact;
    var parentSku = opts.parentSku;
    var kind = opts.kind;

    var imageHtml = item.image
      ? '<img class="cart-drawer__item-image" src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy">'
      : placeholderSvg();
    var imageWrapClass = '';

    var variantHtml = '';
    if (item.variant_title && item.variant_title !== 'Default Title') {
      variantHtml = '<p class="cart-drawer__item-variant">' + escapeHtml(item.variant_title) + '</p>';
    }

    // Eyebrow on a companion sub-line: spells out, in plain words,
    // which parent the line belongs to. `kind` (from
    // properties[_companion_kind], set at co-add time in
    // sections/product-variants-table.liquid) distinguishes the two
    // current companion types — calibration certs and weights/baridia —
    // so we can label them correctly. Legacy carts (no _companion_kind)
    // fall back to "Διακρίβωση για" since cert was the only companion
    // type before the varidio drawer landed.
    var eyebrowHtml = '';
    // WORK-259 (Look 1): the companion line is a compact sub-line under its instrument. It carries
    // the word («Διακρίβωση» / «Βαρίδιο») for the shopper's drawer, where the indentation already
    // says which instrument, and the chip «ΔΙΑΚΡΙΒΩΣΗ ΓΙΑ <sku>» for the staff cart, where staff
    // read by code; CSS shows one or the other (look1-cart.css). Strings: CartDrawerStrings.
    var isSub = !!parentSku && kind !== 'varidio';
    if (parentSku) {
      var wordText, chipText, iconSvg, kindClass;
      if (kind === 'varidio') {
        wordText = STRINGS.weight || 'Βαρίδιο';
        chipText = STRINGS.weightChip || 'ΒΑΡΙΔΙΟ ΓΙΑ';
        iconSvg = '<svg class="cart-drawer__item-eyebrow-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="9" r="3"/><path d="M5 21 7 11h10l2 10z"/></svg>';
        kindClass = ' cart-drawer__item-eyebrow--varidio';
      } else {
        wordText = STRINGS.certificate || 'Διακρίβωση';
        chipText = STRINGS.certificateChip || 'ΔΙΑΚΡΙΒΩΣΗ ΓΙΑ';
        iconSvg = '<svg class="cart-drawer__item-eyebrow-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="9" r="5"/><path d="M8.5 13.5L7 21l5-2 5 2-1.5-7.5"/></svg>';
        kindClass = '';
      }
      eyebrowHtml = '<p class="cart-drawer__item-eyebrow' + kindClass + '">' +
        iconSvg +
        '<span class="cart-drawer__item-eyebrow-word">' + escapeHtml(wordText) + '</span>' +
        '<span class="cart-drawer__item-eyebrow-chip">' + escapeHtml(chipText) + ' <strong>' + escapeHtml(parentSku) + '</strong></span>' +
      '</p>';
    }
    // ORG-280 «10 for 9»: a hidden box-of-10 variant (recognised by its handle
    // <piece handle>-box10; SKU <code>-BOX10, legacy -B10; priced 9).
    // Label says how many boxes/pieces the quantity means, and the line links
    // to the (visible) piece product instead of the hidden box product.
    var itemUrl = item.url;
    var isBox10 = !!(window.Box10 && window.Box10.isBoxItem && window.Box10.isBoxItem(item));
    if (isBox10) {
      var B = window.Box10;
      var lbl = (B && B.boxLabel) ? B.boxLabel(item.quantity) : { boxes: item.quantity + ' × κουτί 10\u00A0τεμ.', sealed: 'σφραγισμένο' };
      eyebrowHtml = '<p class="box10-tag">' + escapeHtml(lbl.boxes) +
        ' · <strong>' + escapeHtml(lbl.sealed) + '</strong></p>';
      if (B && B.pieceUrl) itemUrl = B.pieceUrl(item.url, B.pieceFor ? B.pieceFor(item.variant_id) : null);
      // Thumbnail stays the line's own photo (the box product copies the
      // piece's image, so the customer sees the insert they picked), with a
      // dark «×10» corner chip (John's pick over the case sketch and a 📦,
      // 2026-09-23).
      imageHtml += '<span class="box10-thumb-badge" aria-hidden="true">×10</span>';
      imageWrapClass = ' box10-thumb';
    }
    // ORG-280 nudge: a LOOSE, property-free line of a box-mapped piece with
    // 1–9 pieces → "add N more and get −10% on the whole box". Never on box/companion
    // lines, never for a 20%/Insize customer (Bx.off, ORG-391: no box deal for them).
    var nudgeHtml = '';
    var Bx = window.Box10;
    // ORG-736: an insert line of a family whose gift holder is in the cart is kept loose by
    // the planner (assets/ekptosi-codes.js, PromoGift.held): no box nudge; from 10 pieces a
    // line says why there is no −10% box price.
    var PG = window.PromoGift;
    var giftHeld = !!(PG && PG.held && PG.held(item.variant_id));
    var GS = STRINGS.gift || {};
    if (giftHeld && item.quantity >= 10 && Bx && Bx.map && Bx.map[String(item.variant_id)]) {
      nudgeHtml = '<p class="promo-gift-boxoff" data-gift-boxoff>' + escapeHtml(GS.boxOff || '') + '</p>';
    }
    // The same insert on several lines next to a gift: say they belong together, so nobody
    // deletes one as a duplicate (and loses a gift).
    var split = splitOf[String(item.variant_id)];
    if (giftHeld && split && split.lines > 1 && GS.splitNote) {
      nudgeHtml += '<p class="promo-gift-split" data-gift-split>' +
        escapeHtml(GS.splitNote.replace('__N__', split.lines).replace('__Q__', split.qty)) + '</p>';
    }
    if (!giftHeld && Bx && !Bx.off && Bx.nudgeFor && Bx.map && Bx.map[String(item.variant_id)] &&
        !(window.Box10 && window.Box10.isBoxItem && window.Box10.isBoxItem(item)) &&
        !(item.properties && Object.keys(item.properties).length)) {
      var nudgeN = Bx.nudgeFor(item.quantity);
      if (nudgeN) {
        // Same visual language as the drawer's free-shipping meter:
        // text line, thin orange progress bar (qty/10), count + button.
        var nudgeQ = 10 - nudgeN;
        var B10S = STRINGS.box10 || {};
        nudgeHtml = '<div class="box10-nudge">' +
          '<div class="box10-nudge__head">' +
            '<p class="box10-nudge__text"><span class="box10-nudge__headline">' + escapeHtml(B10S.headline || '−10% σε ολόκληρο κουτί') + '</span>' +
              // nudgeSub is our own translated copy (it carries <strong>), not user input.
              '<span class="box10-nudge__sub">' + (B10S.nudgeSub ? B10S.nudgeSub.replace('__N__', nudgeN) : 'Ακόμη ' + nudgeN + '&nbsp;τεμ. και παίρνετε <strong>−10%</strong> σε όλο το κουτί') + '</span></p>' +
          '</div>' +
          '<div class="box10-nudge__meter" aria-hidden="true"><div class="box10-nudge__progress" style="--width:' + (nudgeQ * 10) + '%"></div></div>' +
          '<div class="box10-nudge__foot">' +
            '<span class="box10-nudge__count">' + escapeHtml(B10S.progress ? B10S.progress.replace('__N__', nudgeQ) : nudgeQ + ' / 10\u00A0τεμ.') + '</span>' +
            '<button type="button" class="box10-fill-btn" data-box10-fill data-line-key="' + escapeHtml(item.key) + '" aria-label="' + escapeHtml(B10S.fillAria || 'Συμπλήρωση σε κουτί 10 τεμ.') + '">' +
              escapeHtml(Bx.fillLabel ? Bx.fillLabel(nudgeN) : '+' + nudgeN + ' → −10%') + '</button>' +
          '</div></div>';
      }
    }

    // SKU (variant SKU). Render as a small tag-style chip under the title
    // so users can verify what they added matches what's on the table row.
    var skuHtml = '';
    if (item.sku) {
      // title: the staff row keeps the code on one line (never broken mid-code, WORK-259) and
      // ellipsises it when the column is narrow, so hovering still shows the whole code.
      skuHtml = '<p class="cart-drawer__item-sku" title="' + escapeHtml(displaySku(item)) + '"><span class="cart-drawer__item-sku-label">SKU:</span> ' + escapeHtml(displaySku(item)) + '</p>';
    }

    // Visible custom properties (skip those starting with `_` — Shopify convention for hidden).
    // A companion sub-line renders none: its only one is the «Σημείωση: Διακρίβωση για SKU …»
    // note, which the indented line under its instrument already says (WORK-259; the property
    // itself stays on the line and reaches the order).
    var propsHtml = '';
    if (!isSub && item.properties && typeof item.properties === 'object') {
      var visible = Object.keys(item.properties).filter(function (k) { return k && k.charAt(0) !== '_' && item.properties[k]; });
      if (visible.length) {
        propsHtml = '<ul class="cart-drawer__item-properties">' +
          visible.map(function (k) { return '<li>' + escapeHtml(k) + ': ' + escapeHtml(item.properties[k]) + '</li>'; }).join('') +
          '</ul>';
      }
    }

    var prices = priceParts(item);

    var itemClass = 'cart-drawer__item' + (isCompact ? ' cart-drawer__item--related' : '') + (isSub ? ' cart-drawer__item--sub' : '');

    // The staff cart used to collapse option values / line properties / unit price behind
    // a per-line expand toggle. Staff never read them while building a cart and asked for
    // the shortest possible row, so all three are now permanently hidden by CSS
    // (.cart-drawer--staff rules in cart-drawer.css) and the toggle is gone — it had
    // nothing left to reveal. They remain visible on /cart and on the Zoho document.
    var expandHtml = '';

    return '<div class="' + itemClass + '" data-cart-line="' + (index + 1) + '" data-cart-key="' + escapeHtml(item.key) + '">' +
      '<a class="cart-drawer__item-image-wrap' + imageWrapClass + '" href="' + escapeHtml(itemUrl) + '" tabindex="-1" aria-hidden="true">' + imageHtml + '</a>' +
      '<div class="cart-drawer__item-details">' +
        eyebrowHtml +
        // title attribute: the staff row truncates hard (SKU, title, stepper and remove all
        // share one line), so hovering must still reveal the full product name.
        '<a class="cart-drawer__item-title" href="' + escapeHtml(itemUrl) + '" title="' + escapeHtml(item.product_title) + '">' + escapeHtml(item.product_title) + '</a>' +
        variantHtml +
        skuHtml +
        propsHtml +
        '<div class="cart-drawer__item-bottom-row">' +
          '<div class="cart-drawer__qty" role="group" aria-label="' + escapeHtml(STRINGS.quantity) + '">' +
            '<button type="button" data-cart-qty-minus aria-label="−">−</button>' +
            '<input class="cart-drawer__qty-value" type="number" min="0" step="1" inputmode="numeric" value="' + item.quantity + '" data-cart-qty-input aria-label="' + escapeHtml(STRINGS.quantity) + '">' +
            '<button type="button" data-cart-qty-plus aria-label="+">+</button>' +
          '</div>' +
          // "Move to Favourites" — saves the variant to the wishlist and drops
          // the line. Skipped on companion lines (cert/varidio): they belong to
          // their parent, saving one alone would strand it.
          (!(item.properties && item.properties._related_to_variant)
            ? '<button type="button" class="cart-drawer__item-save" data-cart-save-later aria-label="' + escapeHtml(STRINGS.saveForLater || '') + '" title="' + escapeHtml(STRINGS.saveForLater || '') + '">' +
                '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z"/></svg>' +
                '<span class="cart-drawer__item-save-label">' + escapeHtml(STRINGS.saveShort || STRINGS.saveForLater || '') + '</span>' +
              '</button>'
            : '') +
          '<button type="button" class="cart-drawer__item-remove" data-cart-remove aria-label="' + escapeHtml(STRINGS.remove) + '" title="' + escapeHtml(STRINGS.remove) + '">' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>' +
          '</button>' +
        '</div>' +
      '</div>' +
      '<div class="cart-drawer__item-price">' +
        '<div class="cart-drawer__item-line-price">' + prices.line + '</div>' +
        prices.unit +
        expandHtml +
      '</div>' +
      // Box nudge spans the whole line (photo + details + price columns), under
      // the product, instead of being squeezed into the details column.
      nudgeHtml +
    '</div>';
  }

  // Render cart items grouped: a parent line, its bundled baridio (compact
  // sub-row tucked under), and its calibration certificate (full-size card
  // — visually "another product" — but still inside the same envelope so
  // the relationship is preserved). All three wrapped in a single
  // .cart-drawer__group. Max group size is 3: tape + varidio + cert.
  // A line counts as a bundled child when properties[_related_to_variant]
  // matches a parent line's variant_id; the visual treatment is then
  // chosen from properties[_companion_kind] (varidio = compact;
  // diakrivosi = full-size; legacy/unknown = full-size to be safe).
  function renderItems(cart) {
    var items = cart.items || [];
    splitOf = {};
    items.forEach(function (it) {
      var k = String(it.variant_id), e = splitOf[k] || (splitOf[k] = { lines: 0, qty: 0 });
      e.lines += 1;
      e.qty += it.quantity;
    });
    // index → original 1-based cart line number (Shopify cart line)
    var withLine = items.map(function (it, i) { return { item: it, line: i + 1 }; });

    // variant_id → array of related entries
    var relatedByParent = {};
    withLine.forEach(function (e) {
      var rel = e.item.properties && e.item.properties._related_to_variant;
      if (rel) {
        var key = String(rel);
        (relatedByParent[key] = relatedByParent[key] || []).push(e);
      }
    });

    // Set of related-line keys so we don't double-render them.
    var relatedKeys = {};
    Object.keys(relatedByParent).forEach(function (k) {
      relatedByParent[k].forEach(function (e) { relatedKeys[e.item.key] = true; });
    });

    // Collect each top-level line/group as its own HTML block so the order can
    // be flipped in one place below.
    var blocks = [];
    withLine.forEach(function (e) {
      if (relatedKeys[e.item.key]) return; // skip, will render under parent
      var children = relatedByParent[String(e.item.variant_id)] || [];
      var block;
      if (children.length) {
        var parentSku = displaySku(e.item) || '';
        block = '<div class="cart-drawer__group">';
        block += renderItem(e.item, e.line - 1);
        // Order children so the cert renders LAST (after the tape and the
        // bundled baridio) — the cert is the most product-like of the
        // three and reads better as the bottom card in the envelope.
        var ordered = children.slice().sort(function (a, b) {
          var ka = (a.item.properties && a.item.properties._companion_kind) || '';
          var kb = (b.item.properties && b.item.properties._companion_kind) || '';
          var rank = function (k) { return k === 'varidio' ? 0 : 1; };
          return rank(ka) - rank(kb);
        });
        ordered.forEach(function (c) {
          var childKind = (c.item.properties && c.item.properties._companion_kind) || '';
          block += renderItem(c.item, c.line - 1, {
            isCompact: childKind === 'varidio',
            parentSku: parentSku,
            kind: childKind
          });
        });
        block += '</div>';
      } else {
        block = renderItem(e.item, e.line - 1);
      }
      blocks.push(block);
    });
    // Shopify returns cart.items newest-first (a freshly added line is prepended).
    // For staff, show them chronologically instead — first-added on top, newest at
    // the bottom — which also flows into the Zoho document, whose line order
    // follows this drawer's DOM order. Customer carts keep Shopify's default order.
    if (window.STAFF_ORDER) blocks.reverse();
    var html = blocks.join('');

    // Any orphan related items whose parent isn't in the cart any more —
    // render standalone so the user can still remove them.
    withLine.forEach(function (e) {
      if (!relatedKeys[e.item.key]) return;
      var parentVid = String(e.item.properties._related_to_variant);
      var parentInCart = withLine.some(function (x) { return !x.item.properties || !x.item.properties._related_to_variant; }) && withLine.some(function (x) { return String(x.item.variant_id) === parentVid; });
      if (!parentInCart) {
        html += renderItem(e.item, e.line - 1);
      }
    });

    return html;
  }

  // An empty cart drops a pending staff confirm. The header «Άδειασμα» itself is
  // shown by CSS from the line list, which also holds staff extra lines.
  function setHeadClear(hasItems) {
    if (!hasItems) hideClearConfirm();
  }
  // WORK-259 (Look 1): the count pill beside the drawer title.
  function setCountPill(cart) {
    var pill = $('[data-cart-drawer-count]');
    if (!pill) return;
    var n = (cart && cart.item_count) || 0;
    pill.textContent = String(n);
    pill.setAttribute('aria-label', String(STRINGS.countAria || '__N__').replace('__N__', String(n)));
    pill.hidden = n === 0;
  }

  function renderBody(cart) {
    // WORK-266: a line's tags (zitiste_timi) arrive async; once fetched, draw again so the line
    // reads «Ζητήστε Προσφορά» (the second pass finds nothing left to fetch, so no loop).
    if (window.QUOTE_ONLY) window.QUOTE_ONLY.lines(cart).then(function (r) { if (r.fetched) rerender(); });
    var body = $('[data-cart-drawer-body]');
    var footer = $('[data-cart-drawer-footer]');
    if (!body) return;

    setCountPill(cart);
    if (!cart || !cart.items || cart.items.length === 0) {
      // Cross-promo: an empty cart is the moment to remind the buyer their
      // saved items exist (and reintroduce the wishlist feature itself).
      var wlCount = (window.Wishlist && window.Wishlist.count) ? window.Wishlist.count() : 0;
      var wlPromo = '';
      if (wlCount > 0) {
        wlPromo = '<div class="cart-drawer__wishlist-promo">' +
          '<p class="cart-drawer__wishlist-promo-text">' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="#e74c3c" stroke="#e74c3c" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z"/></svg>' +
            escapeHtml(wlCount === 1
              ? String(STRINGS.wishlistPromoOne || STRINGS.wishlistPromo || '').replace('__N__', '1')
              : String(STRINGS.wishlistPromo || '').replace('__N__', String(wlCount))) +
          '</p>' +
          '<button type="button" class="cart-drawer__wishlist-promo-btn" data-cart-open-wishlist>' + escapeHtml(STRINGS.wishlistPromoCta || '') + '</button>' +
        '</div>';
      }
      body.innerHTML = '<div class="cart-drawer__empty">' +
        '<div class="cart-drawer__empty-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg></div>' +
        '<p class="cart-drawer__empty-title">' + escapeHtml(STRINGS.empty) + '</p>' +
        '<button type="button" class="cart-drawer__empty-btn" data-drawer-close>' + escapeHtml(STRINGS.continueShopping) + '</button>' +
        wlPromo +
        '</div>';
      // Staff keep the footer (customer picker, discount, doc setup — the
      // staff-order panel lives there) even with an empty cart: «Add Shopify»
      // lands here to set the order up BEFORE any product is added, and
      // hiding it left staff staring at a bare "cart is empty" with the
      // customer they'd just selected nowhere in sight. A real customer's
      // empty cart still hides the footer as before.
      if (footer) footer.hidden = !window.STAFF_ORDER;
      setHeadClear(false);
      return;
    }

    // ORG-736: the gift-promo nudges the planner fetched for this cart (server-rendered copy).
    var giftBlock = (window.PromoGift && window.PromoGift.html) ? window.PromoGift.html() : '';
    body.innerHTML = giftBlock + '<div class="cart-drawer__items">' + renderItems(cart) + '</div>';

    setHeadClear(true);
    if (footer) {
      footer.hidden = false;
      // Subtotal = pre-VAT price (Shopify prices are stored without tax for
      // tax-exclusive stores like Organa). Greek VAT rate is 24%.
      // ORG-528: outside Greece the subtotal is already final (that market's VAT is
      // in it, or no tax applies): nothing to add, the ΦΠΑ row is not rendered.
      var subtotalCents = cart.items_subtotal_price || cart.total_price;
      var vatRate = isGreece() ? 0.24 : 0;
      var vatCents = Math.round(subtotalCents * vatRate);
      var totalCents = subtotalCents + vatCents;
      var subtotal = $('[data-cart-drawer-subtotal]', footer);
      var vat = $('[data-cart-drawer-vat]', footer);
      var total = $('[data-cart-drawer-total]', footer);
      if (subtotal) subtotal.textContent = formatMoney(subtotalCents);
      if (vat) vat.textContent = formatMoney(vatCents);
      if (total) total.textContent = formatMoney(totalCents);

      renderFreeShip(subtotalCents);
    }
  }

  // Free-shipping progress bar. The configured threshold is ex-VAT (it
  // mirrors the real discount, which triggers on the ex-VAT subtotal), but
  // we display everything VAT-INCLUSIVE so the bar speaks the same "€80 με
  // ΦΠΑ" language as the announcement — €64.52 ex × 1.24 = €80 incl. Once
  // reached, swaps to the "you earned it" message + green fill.
  function renderFreeShip(subtotalCents) {
    var cfg = window.CartDrawerFreeShip;
    var wrap = $('[data-cart-drawer-freeship]');
    if (!wrap) return;
    var threshold = cfg && Number(cfg.threshold);
    if (!cfg || !cfg.enabled || !threshold || threshold <= 0) { wrap.hidden = true; return; }
    wrap.hidden = false;

    var vatMult = 1.24;
    var thresholdIncl = Math.round(threshold * vatMult);
    var subtotalIncl = Math.round(subtotalCents * vatMult);
    var remainder = thresholdIncl - subtotalIncl;
    var progress = Math.max(0, Math.min(100, Math.round((subtotalIncl / thresholdIncl) * 100)));
    var statusEl = $('[data-cart-drawer-freeship-status]', wrap);
    var barEl = $('[data-cart-drawer-freeship-bar]', wrap);

    if (statusEl) {
      if (remainder > 0) {
        statusEl.innerHTML = String(cfg.ineligibleHtml || '').replace(/\{\{\s*amount\s*\}\}/g, formatMoney(remainder));
        wrap.classList.remove('is-complete');
      } else {
        statusEl.innerHTML = String(cfg.eligibleHtml || '');
        wrap.classList.add('is-complete');
      }
    }
    if (barEl) barEl.style.setProperty('--width', progress + '%');
  }

  // Update the header cart count badge + add a brief pop animation on increase.
  var lastCount = null;
  function updateHeaderBadge(cart) {
    var count = cart ? cart.item_count : 0;
    var badge = $('[data-header-cart-count]');
    if (!badge) return;
    if (count > 0) {
      badge.setAttribute('data-header-cart-count', String(count));
      badge.classList.add('visible');
    } else {
      badge.removeAttribute('data-header-cart-count');
      badge.setAttribute('data-header-cart-count', '');
      badge.classList.remove('visible');
    }
    if (lastCount !== null && count > lastCount) {
      badge.classList.remove('is-bumping');
      void badge.offsetWidth;
      badge.classList.add('is-bumping');
    }
    lastCount = count;
  }

  function setLoading(loading) {
    var body = $('[data-cart-drawer-body]');
    if (!body) return;
    if (loading && !state.cart) {
      body.innerHTML = '<div class="cart-drawer__loading"><div class="cart-drawer__spinner" aria-hidden="true"></div></div>';
    }
  }

  function refresh() {
    return fetch(ROOT + 'cart.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        state.cart = cart;
        renderBody(cart);
        updateHeaderBadge(cart);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        return cart;
      })
      .catch(function (err) { console.error('[CartDrawer] refresh failed', err); });
  }

  // ORG-299: +/− feel instant. Shopify's /cart/change.js takes ~350 ms, and the
  // drawer used to wait for it AND drop taps made meanwhile (3 quick taps →
  // +1). Now a tap only records the line's target quantity in pendingQty and
  // re-renders from viewCart() (the cart with pending quantities patched in);
  // ~300 ms after a line's last tap ONE request sends that absolute quantity.
  // The server's cart replaces the guess when it answers (it may cap stock).
  var pendingQty = {};
  var flushTimers = {};
  var flushing = false;
  var FLUSH_MS = 300;
  function findItem(cart, key) {
    if (!cart || !cart.items) return null;
    for (var i = 0; i < cart.items.length; i++) if (cart.items[i].key === key) return cart.items[i];
    return null;
  }
  function viewCart() {
    var c = state.cart;
    if (!c || !c.items || !Object.keys(pendingQty).length) return c;
    var sub = c.items_subtotal_price != null ? c.items_subtotal_price : c.total_price;
    var count = c.item_count || 0;
    var items = [];
    c.items.forEach(function (it) {
      if (!(it.key in pendingQty)) { items.push(it); return; }
      var q = pendingQty[it.key], dq = q - it.quantity;
      sub += dq * it.final_price;
      count += dq;
      if (q > 0) {
        items.push(Object.assign({}, it, {
          quantity: q, final_line_price: it.final_price * q,
          original_line_price: it.original_price * q, line_price: it.price * q
        }));
      }
    });
    return Object.assign({}, c, { items: items, item_count: count, items_subtotal_price: sub, total_price: sub });
  }
  function renderView() {
    var v = viewCart();
    renderBody(v);
    updateHeaderBadge(v);
    Object.keys(pendingQty).forEach(function (k) {
      var el = document.querySelector('.cart-drawer__item[data-cart-key="' + (window.CSS && CSS.escape ? CSS.escape(k) : k) + '"]');
      if (el) el.classList.add('is-syncing');
    });
  }
  function setQty(key, q) {
    var it = findItem(state.cart, key);
    if (!it) return;
    if (q === it.quantity) delete pendingQty[key]; else pendingQty[key] = q;
    renderView();
    clearTimeout(flushTimers[key]);
    if (key in pendingQty) flushTimers[key] = setTimeout(function () { flush(key); }, FLUSH_MS);
  }
  function flushNext() {
    // A line whose timer already fired while another request was in flight.
    for (var k in pendingQty) if (!flushTimers[k]) { flush(k); return; }
  }
  function flush(key) {
    delete flushTimers[key];
    if (!(key in pendingQty)) return;
    var B = window.Box10;
    if (flushing || state.inflight || (B && B.busy)) {
      flushTimers[key] = setTimeout(function () { flush(key); }, 120);
      return;
    }
    var q = pendingQty[key];
    var it = findItem(state.cart, key);
    // A box-mapped piece reaching 10+: convert straight to box(es) with ONE
    // /cart/update.js (Box10.normalize on the patched cart) instead of change
    // → cart:updated → update → refresh.
    var view = viewCart();
    if (it && q >= 10 && B && B.plan && B.map && B.map[String(it.variant_id)] && B.plan(view)) {
      flushing = true;
      delete pendingQty[key];
      return B.normalize(view).finally(function () { flushing = false; flushNext(); });
    }
    flushing = true;
    fetch(ROOT + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: q })
    })
      .then(function (r) {
        // 422 (e.g. more than in stock): drop the guess, resync from the server.
        if (!r.ok) { delete pendingQty[key]; return refresh(); }
        return r.json().then(function (cart) {
          if (pendingQty[key] === q) delete pendingQty[key];
          state.cart = cart;
          renderView();
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        });
      })
      .catch(function (err) { delete pendingQty[key]; console.error('[CartDrawer] change failed', err); return refresh(); })
      .finally(function () { flushing = false; flushNext(); });
  }

  // `line`: a 1-based line index (CartDrawer.change API, variants table) or a
  // line KEY (string; the drawer's own controls — keys survive an optimistic
  // re-render that hides a line, indexes don't).
  function changeLine(line, quantity) {
    if (state.inflight) return Promise.resolve();
    state.inflight = true;
    var target = typeof line === 'string' ? { id: line } : { line: line };
    if (typeof line === 'string') delete pendingQty[line];
    return fetch(ROOT + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body: JSON.stringify(Object.assign(target, { quantity: quantity }))
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        state.cart = cart;
        renderBody(cart);
        updateHeaderBadge(cart);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        return cart;
      })
      .finally(function () { state.inflight = false; });
  }

  function addItem(payload) {
    // payload: { id, quantity, properties } or a FormData
    var body, headers;
    if (payload instanceof FormData) {
      body = payload;
      headers = { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' };
    } else {
      body = JSON.stringify(payload);
      headers = { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    }
    return fetch(ROOT + 'cart/add.js', { method: 'POST', headers: headers, credentials: 'same-origin', body: body })
      .then(function (r) { return r.json(); })
      .then(function (added) {
        // Always refresh to get the canonical cart back
        return refresh().then(function () { return added; });
      });
  }

  function open() { if (window.SiteDrawer) window.SiteDrawer.open('cart-drawer'); }
  function close() { if (window.SiteDrawer) window.SiteDrawer.close('cart-drawer'); }

  // Delegated handlers for in-drawer qty controls.
  document.addEventListener('click', function (e) {
    var item = e.target.closest('.cart-drawer__item');
    if (!item) return;

    var line = item.getAttribute('data-cart-key');
    if (!line) return;
    var it1 = findItem(state.cart, line);
    var cur = line in pendingQty ? pendingQty[line] : (it1 ? it1.quantity : 0);

    if (e.target.closest('[data-cart-qty-plus]')) {
      e.preventDefault();
      setQty(line, cur + 1);
      return;
    }
    if (e.target.closest('[data-cart-qty-minus]')) {
      var it2 = it1;
      e.preventDefault();
      // A box line's «−» takes ONE piece off, not the whole box: 1 box → 9
      // loose (Box10.minusOne, the variants table's stepper logic; ORG-297).
      // The bin still removes the whole box.
      var Bm = window.Box10;
      var piece = it2 && Bm && Bm.isBoxItem && Bm.isBoxItem(it2) && Bm.pieceFor ? Bm.pieceFor(it2.variant_id) : null;
      if (piece) { Bm.minusOne(piece, state.cart); return; }
      setQty(line, Math.max(0, cur - 1));
      return;
    }
    if (e.target.closest('[data-cart-save-later]')) {
      e.preventDefault();
      var it = it1;
      if (it && window.Wishlist) {
        // Suppress the wishlist drawer's add-auto-open: the user is working in
        // the CART drawer; the badge bump + the line vanishing are the feedback.
        window.__wishlistSuppressAutoOpen = true;
        try { window.Wishlist.add(it.variant_id, it.handle); }
        finally { setTimeout(function () { window.__wishlistSuppressAutoOpen = false; }, 0); }
      }
      changeLine(line, 0);
      return;
    }
    if (e.target.closest('[data-cart-remove]')) {
      e.preventDefault();
      changeLine(line, 0);
      return;
    }
  });

  // Empty-state promo CTA: swap this drawer for the wishlist drawer.
  // Programmatic open bypasses the drawer's [data-drawer-open] click wrapper,
  // so its render hook must be called explicitly or it spins on "loading".
  document.addEventListener('click', function (e) {
    if (!e.target.closest('[data-cart-open-wishlist]')) return;
    e.preventDefault();
    close();
    setTimeout(function () {
      if (window.SiteDrawer) window.SiteDrawer.open('wishlist-drawer');
      if (window.__wishlistDrawerRender) window.__wishlistDrawerRender();
    }, 200);
  });

  // Keep the empty-state promo count fresh while the drawer shows it.
  document.addEventListener('wishlist:changed', function () {
    if (state.cart && (!state.cart.items || state.cart.items.length === 0)) {
      renderBody(state.cart);
    }
  });

  // "Empty cart" — destructive action, confirm-on-click, calls /cart/clear.js
  // then refreshes. Shoppers: the quiet footer link + window.confirm. Staff
  // (ORG-772): the red header button, confirmed by the inline row under the
  // header ([data-cart-clear-confirm]) instead of a browser dialog.
  function isStaffDrawer() {
    var d = document.getElementById('cart-drawer');
    return !!(d && d.classList.contains('cart-drawer--staff') && $('[data-cart-clear-confirm]'));
  }
  function hideClearConfirm() {
    var row = $('[data-cart-clear-confirm]');
    if (row) row.hidden = true;
  }
  function clearCart(btn) {
    if (state.inflight) return;
    // Read at click time — staff-order.js publishes this on DOMContentLoaded,
    // after this delegated listener is bound.
    var staff = window.StaffOrder;
    state.inflight = true;
    btn.disabled = true;
    fetch(ROOT + 'cart/clear.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    })
      // clear.js zeroes line items only. For staff, the panel state and the ΑΦΜ
      // cart attributes outlive it — reset those before re-rendering.
      .then(function () { return staff && staff.reset ? staff.reset() : null; })
      .then(function () { return refresh(); })
      .catch(function (err) { console.error('[CartDrawer] clear failed', err); })
      .finally(function () { state.inflight = false; btn.disabled = false; hideClearConfirm(); });
  }
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-cart-clear]');
    if (!btn) return;
    e.preventDefault();
    if (state.inflight) return;
    if (isStaffDrawer()) {
      var row = $('[data-cart-clear-confirm]');
      row.hidden = !row.hidden;
      if (!row.hidden) { var no = $('[data-cart-clear-no]', row); if (no) no.focus(); }
      return;
    }
    var staff = window.StaffOrder;
    if (!confirm((staff && staff.clearConfirm) || STRINGS.clearConfirm || 'Είστε σίγουρος ότι θέλετε να αδειάσετε το καλάθι;')) return;
    clearCart(btn);
  });
  document.addEventListener('click', function (e) {
    var yes = e.target.closest('[data-cart-clear-yes]');
    if (yes) { e.preventDefault(); clearCart(yes); return; }
    if (e.target.closest('[data-cart-clear-no]')) { e.preventDefault(); hideClearConfirm(); }
  });

  // Manual quantity edits via the typed input — commit on Enter / blur.
  document.addEventListener('change', function (e) {
    if (!e.target.matches('[data-cart-qty-input]')) return;
    var item = e.target.closest('.cart-drawer__item');
    if (!item) return;
    var line = item.getAttribute('data-cart-key');
    if (!line) return;
    var v = parseInt(e.target.value, 10);
    if (isNaN(v) || v < 0) v = 0;
    changeLine(line, v);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' && e.target.matches('[data-cart-qty-input]')) {
      e.preventDefault();
      e.target.blur();
    }
  });

  // Refresh on cart-mutation events from other components (variants table,
  // legacy theme code, third-party). Debounced so a burst of events (e.g.
  // variant + calibration co-add) only triggers one fetch.
  var refreshTimer = null;
  function scheduleRefresh() {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () { refreshTimer = null; refresh(); }, 120);
  }
  // 'product-form:submit:success' is the signal that the user JUST added
  // an item (vs a passive refresh from any other source) — refresh AND
  // auto-open the drawer so the user sees the cart they just affected
  // (mirrors the wishlist drawer behaviour).
  function refreshAndOpen() {
    scheduleRefresh();
    setTimeout(function () { open(); }, 180);
  }
  document.addEventListener('product-form:submit:success', refreshAndOpen);
  document.documentElement.addEventListener('product-form:submit:success', refreshAndOpen);
  // Passive refresh signals (no auto-open): cart:refresh / cart:request-refresh
  // / cartcount:update fire on background sync, drawer-driven +/-, etc.
  ['cart:refresh', 'cart:request-refresh'].forEach(function (evt) {
    document.addEventListener(evt, scheduleRefresh);
    document.documentElement.addEventListener(evt, scheduleRefresh);
  });
  window.addEventListener('cartcount:update', scheduleRefresh);

  // Initial fetch + render once site-drawer.js has registered.
  function init() {
    setLoading(true);
    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // rerender(): redraw from the cart already held, no fetch (box10.js calls it
  // once its lazily loaded map arrives).
  function rerender() { if (state.cart) renderBody(state.cart); }
  window.CartDrawer = { open: open, close: close, refresh: refresh, rerender: rerender, priceParts: priceParts, add: addItem, change: changeLine, get cart() { return state.cart; } };
})();
